'use client';

import { SliceZone } from '@prismicio/react';
import { useRouter } from 'next/navigation';
import {
  FieldErrors,
  FieldValues,
  SubmitHandler,
  UseFormRegister,
  UseFormWatch,
  useForm,
} from 'react-hook-form';
import { GetStartedDocument } from '../../../prismicio-types';
import { ContentRelationshipField } from '@prismicio/client';
import { useMultiFormStore } from '@/store';
import { useEffect, useRef } from 'react';
import { registerContact } from '@/actions';
import cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { getGetStartedComponents } from '@/slices/component-factory';

const FIRST_STEP = 1;

type GetStartedField = ContentRelationshipField<'get_started'> &
  GetStartedDocument;

interface MultiStepFormProps {
  document: GetStartedDocument;
  hubspotPortalIdForm: string;
}

enum CONTENT_STRUCTURE {
  singleColumn = 'single-column',
  twoColumn = 'two-column',
}

type GetStartedSlices = GetStartedDocument['data']['slices'];
type SliceComponents = ReturnType<typeof getGetStartedComponents>;

interface NavigationButtonsProps {
  errors: FieldErrors<FieldValues>;
  isSubmitting: boolean;
  isLastStep: boolean;
  onBack: () => void;
  onContinue: () => void;
}

const NavigationButtons = ({
  errors,
  isSubmitting,
  isLastStep,
  onBack,
  onContinue,
}: NavigationButtonsProps) => {
  return (
    <div className="flex gap-x-6">
      <button type="button" onClick={onBack} className="button-outline-green">
        Back
      </button>
      <button
        className={`button-filled-green transition-opacity ${
          Object.keys(errors).length > 0 || isSubmitting
            ? 'opacity-50 cursor-not-allowed'
            : ''
        }`}
        type="button"
        onClick={onContinue}
        disabled={Object.keys(errors).length > 0 || isSubmitting}
        aria-label="Continue or Submit form"
      >
        {isLastStep ? 'Submit' : 'Continue'}
      </button>
    </div>
  );
};

interface InformationProps {
  stepNumber: GetStartedDocument['data']['step_number'];
  totalSteps: GetStartedDocument['data']['total_steps'];
  formTitle: GetStartedDocument['data']['form_title'];
  formDescription: GetStartedDocument['data']['form_description'];
}

const Information = ({
  stepNumber,
  totalSteps,
  formTitle,
  formDescription,
}: InformationProps) => {
  return (
    <>
      <p className="text-regular-semibold">
        Step {stepNumber}/{totalSteps}
      </p>
      <h1 className="text-h2">{formTitle}</h1>
      <p className="text-medium-normal">{formDescription}</p>
    </>
  );
};

interface LayoutProps extends InformationProps, NavigationButtonsProps {
  slices: GetStartedSlices;
  sliceComponents: SliceComponents;
  register: UseFormRegister<FieldValues>;
  watch: UseFormWatch<FieldValues>;
}

const OneColumnLayout = ({
  stepNumber,
  totalSteps,
  formTitle,
  formDescription,
  slices,
  sliceComponents,
  register,
  errors,
  watch,
  isLastStep,
  isSubmitting,
  onBack,
  onContinue,
}: LayoutProps): JSX.Element => {
  return (
    <div className="flex flex-col items-center gap-y-12">
      <div className="flex flex-col gap-y-4 text-center">
        <Information
          stepNumber={stepNumber}
          totalSteps={totalSteps}
          formTitle={formTitle}
          formDescription={formDescription}
        />
      </div>
      <div className="w-full flex flex-col gap-y-12">
        <SliceZone
          slices={slices}
          components={sliceComponents}
          context={{ register, errors, watch }}
        />
      </div>
      <NavigationButtons
        errors={errors}
        isSubmitting={isSubmitting}
        isLastStep={isLastStep}
        onBack={onBack}
        onContinue={onContinue}
      />
    </div>
  );
};

const TwoColumnLayout = ({
  stepNumber,
  totalSteps,
  formTitle,
  formDescription,
  slices,
  sliceComponents,
  register,
  errors,
  watch,
  isLastStep,
  isSubmitting,
  onBack,
  onContinue,
}: LayoutProps): JSX.Element => {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-20">
      <div className="flex flex-col gap-y-8 text-left">
        <Information
          stepNumber={stepNumber}
          totalSteps={totalSteps}
          formTitle={formTitle}
          formDescription={formDescription}
        />
        <SliceZone
          slices={slices.filter((slice) => slice.slice_type === 'inputs')}
          components={sliceComponents}
          context={{ register, errors, watch }}
        />
        <NavigationButtons
          errors={errors}
          isSubmitting={isSubmitting}
          isLastStep={isLastStep}
          onBack={onBack}
          onContinue={onContinue}
        />
      </div>
      <SliceZone
        slices={slices.filter((slice) => slice.slice_type !== 'inputs')}
        components={sliceComponents}
        context={{ register, errors, watch }}
      />
    </div>
  );
};

export const MultiStepForm = ({
  document,
  hubspotPortalIdForm,
}: MultiStepFormProps) => {
  const sliceComponents = getGetStartedComponents();
  const router = useRouter();

  const {
    form_title: formTitle,
    step_number: stepNumber,
    form_description: formDescription,
    total_steps: totalSteps,
    slices,
    content_structure: contentStructure,
    next_step: nextStep,
    previous_step: previousStep,
    hubspot_form_id: hubspotFormId,
  } = document.data;

  const typedNextStep = nextStep as GetStartedField;
  const typedPreviousStep = previousStep as GetStartedField;

  const { setFormData, formData, clearForm } = useMultiFormStore();

  const {
    register,
    watch,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm({ mode: 'onBlur', defaultValues: formData });

  const hasRedirected = useRef(false);

  useEffect(() => {
    if (
      Object.keys(formData).length === 0 &&
      typedPreviousStep?.uid &&
      !hasRedirected.current
    ) {
      router.push(typedPreviousStep.url!);
      hasRedirected.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLastStep = stepNumber === totalSteps;

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    const properties: FieldValues = {};
    Object.keys(data).forEach((key) => {
      if (data[key]) {
        properties[key] = data[key];
      }
    });

    if (properties.selectedTechnologies) {
      properties.roles = [
        ...properties.selectedTechnologies,
        ...(properties.roles ? [properties.roles] : []),
      ].join(', ');
      delete properties.selectedTechnologies;
    }

    if (properties.additional) {
      properties.scope = `${properties.scope}, ${properties.additional}`;
      delete properties.additional;
    }

    try {
      const { postSubmitAction } = await registerContact({
        properties,
        pageUri: `https://devsu.com${window.location.pathname}`,
        pageName: window.document.title,
        hubspotPortalIdForm,
        hubspotGuidForm: hubspotFormId as string,
        hubspotutkCookie: cookies.get('hubspotutk'),
      });
      if (postSubmitAction?.type === 'redirect' && postSubmitAction.value) {
        window.location.assign(postSubmitAction.value);
        return;
      }
      router.push(typedNextStep.url!);
      clearForm();
    } catch {
      toast.error('Something went wrong!');
    }
  };

  const handleContinue: SubmitHandler<FieldValues> = (data) => {
    setFormData(data);
    router.push(typedNextStep.url!);
  };

  const handleBack = () => {
    router.back();
    if (stepNumber === FIRST_STEP) {
      clearForm();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {contentStructure === CONTENT_STRUCTURE.singleColumn && (
        <OneColumnLayout
          stepNumber={stepNumber}
          totalSteps={totalSteps}
          formTitle={formTitle}
          formDescription={formDescription}
          slices={slices}
          sliceComponents={sliceComponents}
          register={register}
          errors={errors}
          watch={watch}
          isLastStep={isLastStep}
          isSubmitting={isSubmitting}
          onBack={handleBack}
          onContinue={handleSubmit(isLastStep ? onSubmit : handleContinue)}
        />
      )}
      {contentStructure === CONTENT_STRUCTURE.twoColumn && (
        <TwoColumnLayout
          stepNumber={stepNumber}
          totalSteps={totalSteps}
          formTitle={formTitle}
          formDescription={formDescription}
          slices={slices}
          sliceComponents={sliceComponents}
          register={register}
          errors={errors}
          watch={watch}
          isLastStep={isLastStep}
          isSubmitting={isSubmitting}
          onBack={handleBack}
          onContinue={handleSubmit(isLastStep ? onSubmit : handleContinue)}
        />
      )}
    </form>
  );
};
